import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { EventCard, LoadingSpinner } from '../';
import './Events.css';

const Events = ({ eventsItems = [], isLoading = false }) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(3);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  useEffect(() => {
    const updateItemsPerPage = () => {
      const width = window.innerWidth;
      if (width < 960) {
        setItemsPerPage(1);
      } else if (width < 1440) {
        setItemsPerPage(2);
      } else {
        setItemsPerPage(3);
      }
      setCurrentPage(0);
    };

    updateItemsPerPage();
    window.addEventListener('resize', updateItemsPerPage);
    return () => window.removeEventListener('resize', updateItemsPerPage);
  }, []);

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && canGoNext) {
      goToNextPage();
    }
    if (isRightSwipe && canGoPrev) {
      goToPrevPage();
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1));
  };

  const goToPrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 0));
  };

  const allEvents = eventsItems || [];

  const totalPages = Math.ceil(allEvents.length / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayEvents = allEvents.slice(startIndex, endIndex);
  const canGoPrev = currentPage > 0;
  const canGoNext = currentPage < totalPages - 1;

  const getVisiblePages = () => {
    const maxDots = 7;
    if (totalPages <= maxDots) {
      return Array.from({ length: totalPages }, (_, i) => i);
    }

    const pages = [];
    const leftOffset = Math.floor(maxDots / 2);
    const rightOffset = Math.floor(maxDots / 2);

    let start = Math.max(0, currentPage - leftOffset);
    let end = Math.min(totalPages - 1, currentPage + rightOffset);

    if (currentPage < leftOffset) {
      end = Math.min(totalPages - 1, maxDots - 1);
    }

    if (currentPage > totalPages - rightOffset - 1) {
      start = Math.max(0, totalPages - maxDots);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  };

  return (
    <section className="events-section">
      <div className="events-container">
        <div className="events-header">
          <h2 className="events-title">{t('events.title')}</h2>
          <Link to="/events" className="events-all-link">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" strokeWidth="2"/>
              <line x1="16" y1="2" x2="16" y2="6" strokeWidth="2" strokeLinecap="round"/>
              <line x1="8" y1="2" x2="8" y2="6" strokeWidth="2" strokeLinecap="round"/>
              <line x1="3" y1="10" x2="21" y2="10" strokeWidth="2"/>
            </svg>
            {t('events.allEvents')}
          </Link>
        </div>

        {isLoading ? (
          <div className="events-scroll-wrapper">
            <LoadingSpinner size="small" />
          </div>
        ) : allEvents.length === 0 ? (
          <div className="events-empty-state">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" strokeWidth="1.5"/>
              <line x1="16" y1="2" x2="16" y2="6" strokeWidth="1.5" strokeLinecap="round"/>
              <line x1="8" y1="2" x2="8" y2="6" strokeWidth="1.5" strokeLinecap="round"/>
              <line x1="3" y1="10" x2="21" y2="10" strokeWidth="1.5"/>
            </svg>
            <p>{t('events.noUpcomingEvents')}</p>
          </div>
        ) : (
          <div className="events-scroll-controls">
            <button
              className={`events-scroll-btn events-scroll-btn-left ${!canGoPrev ? 'disabled' : ''}`}
              onClick={goToPrevPage}
              aria-label="Previous page"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div
              className="events-scroll-wrapper"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className="events-grid">
                {displayEvents.map((event, index) => (
                  <div key={event.id} className="fade-in-stagger">
                    <EventCard event={event} index={index} />
                  </div>
                ))}
              </div>
              {totalPages > 1 && (
                <div className="events-pagination">
                  {getVisiblePages().map((pageIndex) => (
                    <button
                      key={pageIndex}
                      className={`events-pagination-dot ${pageIndex === currentPage ? 'active' : ''}`}
                      onClick={() => setCurrentPage(pageIndex)}
                      aria-label={`Go to page ${pageIndex + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>

            <button
              className={`events-scroll-btn events-scroll-btn-right ${!canGoNext ? 'disabled' : ''}`}
              onClick={goToNextPage}
              aria-label="Next page"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Events;